if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this.lightbox = this.classList.contains('oe-product-lightbox');
        if (!this.lightbox) return;

        this.dialog = this.querySelector('dialog');
        this.content = this.querySelector('.product-media-modal__content');
        this.onImageViewportChange = () => {
          if (!this.dialog.open) return;
          const items = this.mediaItems;
          const index = items.findIndex((media) => media.classList.contains('active'));
          this.loadImage(items[index], 'high');
          this.preloadNextImage(index);
        };
        this.dialog.addEventListener('cancel', (event) => {
          event.preventDefault();
          this.hide();
        });
        this.dialog.addEventListener('click', (event) => {
          const step = event.target.closest('[data-lightbox-step]');
          if (step) return this.changeMedia(Number(step.dataset.lightboxStep));
          if (Date.now() - this.swipedAt < 400) return;
          if (event.target === this.dialog || event.target === this.content) this.hide();
        });
        this.dialog.addEventListener('keydown', (event) => {
          if (event.key === 'Tab') {
            const focusable = getFocusableElements(this.dialog).filter((element) =>
              !element.closest('[inert]') && element.getClientRects().length
            );
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            if (event.shiftKey && (document.activeElement === first || !focusable.includes(document.activeElement))) {
              event.preventDefault();
              last?.focus();
            } else if (!event.shiftKey && (document.activeElement === last || !focusable.includes(document.activeElement))) {
              event.preventDefault();
              first?.focus();
            }
            return;
          }
          if (event.target.closest('input, select, textarea, video, iframe, model-viewer')) return;
          if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
          event.preventDefault();
          this.changeMedia(event.key === 'ArrowRight' ? 1 : -1);
        });
        this.content.addEventListener('pointerdown', (event) => {
          this.swipeStart = event.isPrimary && event.pointerType === 'touch' && event.target.matches('img.active')
            ? { x: event.clientX, y: event.clientY }
            : null;
        });
        this.content.addEventListener('pointercancel', () => { this.swipeStart = null; });
        this.content.addEventListener('pointerup', (event) => {
          const start = this.swipeStart;
          this.swipeStart = null;
          if (!start || !event.isPrimary) return;
          const dx = event.clientX - start.x;
          const dy = event.clientY - start.y;
          if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.5) {
            this.swipedAt = Date.now();
            this.changeMedia(dx < 0 ? 1 : -1);
          }
        });
      }

      hide() {
        if (this.lightbox) {
          if (!this.hasAttribute('open')) return;
          this.dialog.close();
          window.visualViewport?.removeEventListener('resize', this.onImageViewportChange);
          window.removeEventListener('resize', this.onImageViewportChange);
          this.imageLoadVersion = (this.imageLoadVersion || 0) + 1;
          this.removeAttribute('open');
          if (!this.wasScrollLocked) document.body.classList.remove('overflow-hidden');
          window.pauseAllMedia();
          this.openedBy?.focus({ preventScroll: true });
          document.body.dispatchEvent(new CustomEvent('modalClosed'));
          return;
        }
        super.hide();
      }

      show(opener) {
        if (this.lightbox) {
          if (this.dialog.open) return;
          this.openedBy = opener;
          this.swipedAt = 0;
          this.wasScrollLocked = document.body.classList.contains('overflow-hidden');
          document.body.classList.add('overflow-hidden');
          this.setAttribute('open', '');
          const index = this.mediaItems.findIndex((media) => media.dataset.mediaId === opener.dataset.mediaId);
          this.activateMedia(index < 0 ? 0 : index);
          this.dialog.showModal();
          window.visualViewport?.addEventListener('resize', this.onImageViewportChange);
          window.addEventListener('resize', this.onImageViewportChange);
          this.querySelector('[id^="ModalClose-"]').focus({ preventScroll: true });
          return;
        }
        super.show(opener);
        this.showActiveMedia();
      }

      get mediaItems() {
        return Array.from(this.content.children).filter((element) => element.hasAttribute('data-media-id'));
      }

      changeMedia(step) {
        const items = this.mediaItems;
        if (items.length < 2) return;
        const current = items.findIndex((media) => media.classList.contains('active'));
        this.activateMedia((current + step + items.length) % items.length, step);
      }

      activateMedia(index, direction = 1) {
        const items = this.mediaItems;
        const active = items[index];
        if (!active) return;
        window.pauseAllMedia();
        items.forEach((media) => {
          media.classList.toggle('active', media === active);
          media.inert = media !== active;
          media.setAttribute('aria-hidden', String(media !== active));
        });
        this.querySelector('[data-lightbox-index]').textContent = index + 1;
        this.querySelector('[data-lightbox-count]').textContent = items.length;
        this.querySelectorAll('[data-lightbox-step]').forEach((button) => { button.hidden = items.length < 2; });
        this.loadImage(active, 'high');
        this.preloadNextImage(index, direction);
        const template = active.querySelector('template');
        if (active.tagName === 'DEFERRED-MEDIA' && template?.content.querySelector('.js-youtube')) active.loadContent();
      }

      loadImage(image, priority) {
        if (image?.tagName !== 'IMG') return;
        image.dataset.baseSizes ||= image.sizes;
        const scale = window.visualViewport?.scale || 1;
        // Preserve detail when the mobile browser magnifies the lightbox.
        image.sizes = window.innerWidth < 750 && scale > 1
          ? `${Math.ceil((document.documentElement.clientWidth - 24) * scale)}px`
          : image.dataset.baseSizes;
        image.fetchPriority = priority;
        image.loading = 'eager';
      }

      preloadNextImage(index, direction = 1) {
        const version = this.imageLoadVersion = (this.imageLoadVersion || 0) + 1;
        if (navigator.connection?.saveData) return;
        const items = this.mediaItems;
        const active = items[index];
        if (!active || items.length < 2) return;
        const ready = active.tagName === 'IMG' ? active.decode() : Promise.resolve();
        ready.then(() => {
          if (!this.dialog.open || version !== this.imageLoadVersion) return;
          const next = items[(index + direction + items.length) % items.length];
          if (next.tagName !== 'IMG') return;
          this.loadImage(next, 'low');
          next.decode().catch(() => {});
        }).catch(() => {});
      }

      showActiveMedia() {
        this.querySelectorAll(
          `[data-media-id]:not([data-media-id="${this.openedBy.getAttribute('data-media-id')}"])`
        ).forEach((element) => {
          element.classList.remove('active');
        });
        const activeMedia = this.querySelector(`[data-media-id="${this.openedBy.getAttribute('data-media-id')}"]`);
        const activeMediaTemplate = activeMedia.querySelector('template');
        const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;
        activeMedia.classList.add('active');
        activeMedia.scrollIntoView();

        const container = this.querySelector('[role="document"]');
        container.scrollLeft = (activeMedia.width - container.clientWidth) / 2;

        if (
          activeMedia.nodeName == 'DEFERRED-MEDIA' &&
          activeMediaContent &&
          activeMediaContent.querySelector('.js-youtube')
        )
          activeMedia.loadContent();
      }
    }
  );
}
